import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { saveAs } from 'file-saver';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FormsModule } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';
import { ExecutionSalesOrderButtonComponent } from '../../order/sales-order/execution-sales-order-button.component';

import { PosmService } from '../posm.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PosmProject, PosmProjectDeleteEligibility, PosmWorkspaceSummary } from '../models/posm-project.model';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PosmProjectStore } from '../models/posm-execution.models';
import { PosmProjectOverviewComponent } from './posm-project-overview/posm-project-overview.component';
import { PosmProjectTruckAlignmentComponent } from './posm-project-truck-alignment/posm-project-truck-alignment.component';
import { PosmProjectExecutionComponent } from './posm-project-execution/posm-project-execution.component';
import { PosmExecutionCompletionReportsComponent } from './posm-execution-completion-reports/posm-execution-completion-reports.component';
import { PosmProjectProductivityComponent } from './posm-project-productivity/posm-project-productivity.component';
import { PosmProjectDeliveryScheduleComponent } from './posm-project-delivery-schedule/posm-project-delivery-schedule.component';
import { PosmProjectMaterialsComponent } from './posm-project-materials/posm-project-materials.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { TransportPlanningService } from '../../transport-planning/transport-planning.service';

@Component({
    selector: 'app-posm-project-details',
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        TagModule,
        ToastModule,
        TooltipModule,
        BreadcrumbModule,
        TableModule,
        SelectModule,
        InputTextModule,
        FileUploadModule,
        InputNumberModule,
        DatePickerModule,
        InputGroupModule,
        InputGroupAddonModule,
        FormsModule,
        PosmProjectOverviewComponent,
        PosmProjectTruckAlignmentComponent,
        PosmProjectExecutionComponent,
        PosmExecutionCompletionReportsComponent,
        PosmProjectProductivityComponent,
        PosmProjectDeliveryScheduleComponent,
        PosmProjectMaterialsComponent,
        PageShellComponent,
        PageDetailHeroComponent,
        PageDataPanelComponent,
        PageMetricStripComponent,
        ExecutionSalesOrderButtonComponent
    ],
    providers: [MessageService],
    templateUrl: './posm-project-details.component.html',
    styleUrls: ['./posm-project-details.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmProjectDetailsComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private posmService = inject(PosmService);
    private router = inject(Router);
    private sessionStorageService = inject(SessionStorageService);
    private transportPlanningService = inject(TransportPlanningService);
    private messageService = inject(MessageService);
    private permissionService = inject(PermissionService);

    public project = signal<PosmProject | null>(null);
    public projectStores = signal<PosmProjectStore[]>([]);
    public projectStoresLoading = signal<boolean>(false);
    public workspaceSummary = signal<PosmWorkspaceSummary | null>(null);
    public materialItemCount = signal<number>(0);
    public deliveryScheduleCount = signal<number>(0);
    public truckAlignmentReleaseCount = signal<number>(0);
    public executionLinkedDeliveryCount = signal<number>(0);
    public breadcrumbItems = signal<MenuItem[]>([]);
    public homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
    public activeTab = signal<string>('overview');
    public exportLoading = signal<boolean>(false);
    public refreshLoading = signal<boolean>(false);
    public deleteLoading = signal<boolean>(false);
    public deleteEligibility = signal<PosmProjectDeleteEligibility | null>(null);
    readonly overviewTab = viewChild(PosmProjectOverviewComponent);
    readonly materialsTab = viewChild(PosmProjectMaterialsComponent);
    readonly scheduleTab = viewChild(PosmProjectDeliveryScheduleComponent);
    readonly truckTab = viewChild(PosmProjectTruckAlignmentComponent);
    readonly executionTab = viewChild(PosmProjectExecutionComponent);
    readonly reportsTab = viewChild(PosmExecutionCompletionReportsComponent);
    readonly productivityTab = viewChild(PosmProjectProductivityComponent);

    canEditProject(): boolean { return this.permissionService.hasPermission(app_permissions.posm.projects.edit); }
    canDeleteProject(): boolean { return this.permissionService.hasPermission(app_permissions.posm.projects.delete); }
    deleteReason(): string {
        const eligibility = this.deleteEligibility();
        return eligibility ? (eligibility.allowed ? 'Delete this unused execution' : eligibility.blockers.join(', '))
            : 'Checking whether this execution can be deleted';
    }

    public tabs = [
        { id: 'overview', label: 'Overview', icon: 'pi pi-chart-bar' },
        { id: 'materials', label: 'Stands & Materials', icon: 'pi pi-box' },
        { id: 'delivery-schedule', label: 'Delivery Schedule', icon: 'pi pi-calendar' },
        { id: 'truck-alignment', label: 'Truck Alignment', icon: 'pi pi-truck' },
        { id: 'execution', label: 'Execution & Tracking', icon: 'pi pi-check-square' },
        { id: 'completion-reports', label: 'Completion Reports', icon: 'pi pi-file-pdf' },
        { id: 'productivity', label: 'Tasks & Incidents', icon: 'pi pi-list-check' }
    ];
    public detailMetrics = computed(() => {
        const project = this.project();
        const summary = this.workspaceSummary();
        if (!project) {
            return [];
        }
        const totalStores = summary?.totalStores ?? this.projectStores().length ?? 0;
        const openTasks = summary?.openInstallationTasks ?? 0;
        const activeShipments = summary?.activeShipments ?? 0;
        const actionCount = summary?.actionCount ?? 0;

        return [
            {
                label: 'Execution Status',
                value: project.status || 'Draft',
                hint: this.projectJobTypeDisplay(project),
                tone: this.metricToneForProjectStatus(project.status)
            },
            {
                label: 'Target Stores',
                value: totalStores,
                hint: `${summary?.storesMissingCoordinates ?? 0} missing coordinates`,
                tone: totalStores ? 'info' as const : 'warn' as const
            },
            {
                label: 'Execution Tasks',
                value: openTasks,
                hint: `${activeShipments} active shipment${activeShipments === 1 ? '' : 's'}`,
                tone: openTasks ? 'info' as const : 'default' as const
            },
            {
                label: 'Open Actions',
                value: actionCount,
                hint: `${summary?.pendingStrategyApprovals ?? 0} pending approval${summary?.pendingStrategyApprovals === 1 ? '' : 's'}`,
                tone: actionCount ? 'warn' as const : 'success' as const
            }
        ];
    });

    ngOnInit() {
        this.route.params.subscribe(params => {
            const id = params['id'];
            if (id) {
                this.loadProject(id);
            }
        });
        this.route.queryParamMap.subscribe(params => {
            const tab = params.get('tab');
            if (tab && this.tabs.some(row => row.id === tab)) {
                this.activeTab.set(tab);
            }
        });
    }

    loadProject(id: number) {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.refreshLoading.set(false);
            return;
        }

        this.projectStores.set([]);
        this.posmService.getProject(id, companyId).pipe(finalize(() => this.refreshLoading.set(false))).subscribe({
            next: (data) => {
                this.project.set(data);
                this.deleteEligibility.set(null);
                if (this.canDeleteProject() && data.id) {
                    this.posmService.getProjectDeleteEligibility(data.id, data.owningCompanyId || companyId).subscribe({
                        next: eligibility => this.deleteEligibility.set(eligibility),
                        error: () => this.deleteEligibility.set({ allowed: false, blockers: ['Unable to verify project activity'] })
                    });
                }
                this.loadProjectStores(data.id, data.owningCompanyId || companyId);
                this.loadWorkspaceStatus(data.id, data.owningCompanyId || companyId);
                this.loadMaterialsBadge(data.id, data.owningCompanyId || companyId);
                this.loadTruckAlignmentBadge(data.id, data.owningCompanyId || companyId);
                this.breadcrumbItems.set([
                    { label: 'POSM Executions', routerLink: '/posm/executions' },
                    { label: data.projectNumber || 'New Execution' }
                ]);
            },
            error: (err) => {
                console.error(err);
                this.project.set(null);
                this.projectStores.set([]);
                this.materialItemCount.set(0);
                this.deliveryScheduleCount.set(0);
                this.truckAlignmentReleaseCount.set(0);
                this.executionLinkedDeliveryCount.set(0);
            }
        });
    }

    loadWorkspaceStatus(projectId?: number, owningCompanyId?: number) {
        if (!projectId || !owningCompanyId) return;
        this.posmService.getWorkspaceSummary(projectId, owningCompanyId).subscribe({
            next: (summary) => {
                this.workspaceSummary.set(summary);
                this.deliveryScheduleCount.set(summary?.deliveryScheduleCount ?? 0);
                this.executionLinkedDeliveryCount.set(summary?.executionLinkedDeliveryCount ?? 0);
            },
            error: () => {
                this.workspaceSummary.set(null);
                this.deliveryScheduleCount.set(0);
                this.executionLinkedDeliveryCount.set(0);
            }
        });
    }

    loadProjectStores(projectId?: number, owningCompanyId?: number) {
        if (!projectId || !owningCompanyId) return;
        this.projectStoresLoading.set(true);
        this.posmService.getStoresByProject(projectId, owningCompanyId).subscribe({
            next: (stores) => {
                this.projectStores.set(stores || []);
                this.projectStoresLoading.set(false);
            },
            error: (err) => {
                console.error(err);
                this.projectStores.set([]);
                this.projectStoresLoading.set(false);
            }
        });
    }

    setTab(tabId: string) {
        this.activeTab.set(tabId);
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: tabId === 'overview' ? null : tabId },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    goToTruckAlignment() {
        this.setTab('truck-alignment');
    }

    refreshProjectWorkspace() {
        const project = this.project();
        if (!project?.id) return;
        const companyId = project.owningCompanyId || this.sessionStorageService.getCompany()?.id;
        if (!companyId) return;
        this.loadProjectStores(project.id, companyId);
        this.loadWorkspaceStatus(project.id, companyId);
        this.loadMaterialsBadge(project.id, companyId);
        this.loadTruckAlignmentBadge(project.id, companyId);
    }

    refreshView(): void {
        const project = this.project();
        if (!project?.id || this.refreshLoading()) return;
        this.refreshLoading.set(true);
        switch (this.activeTab()) {
            case 'overview': this.overviewTab()?.loadProjectData(project); break;
            case 'materials': this.materialsTab()?.loadProjectData(project); break;
            case 'delivery-schedule': this.scheduleTab()?.loadSchedules(); break;
            case 'truck-alignment': this.truckTab()?.loadData(); break;
            case 'execution': this.executionTab()?.refreshTracking(); break;
            case 'completion-reports': this.reportsTab()?.load(); break;
        }
        this.loadProject(project.id);
    }

    isRefreshBusy(): boolean {
        if (this.refreshLoading()) return true;
        switch (this.activeTab()) {
            case 'overview': return this.overviewTab()?.dataLoading() ?? false;
            case 'materials': return this.materialsTab()?.loading() ?? false;
            case 'delivery-schedule': return this.scheduleTab()?.loading() ?? false;
            case 'truck-alignment': return this.truckTab()?.isLoading() ?? false;
            case 'execution': return !!(this.executionTab()?.isLoading() || this.executionTab()?.gpsLoading());
            case 'completion-reports': return this.reportsTab()?.loading() ?? false;
            case 'productivity': return this.productivityTab()?.loading() ?? false;
            default: return false;
        }
    }

    openLogisticsProject(): void {
        const logisticsProjectId = this.project()?.logisticsProjectId;
        if (logisticsProjectId) {
            this.router.navigate(['/logistics-projects', logisticsProjectId]);
        }
    }

    deleteProject(): void {
        const project = this.project();
        const companyId = project?.owningCompanyId || this.sessionStorageService.getCompany()?.id;
        if (!project?.id || !companyId || !this.canDeleteProject() || !this.deleteEligibility()?.allowed
                || !confirm(`Delete POSM execution ${project.projectNumber}? This cannot be undone.`)) return;
        this.deleteLoading.set(true);
        this.posmService.deleteProject(project.id, companyId).subscribe({
            next: () => this.router.navigate(['/posm/executions']),
            error: err => {
                this.deleteLoading.set(false);
                this.messageService.add({ severity: 'error', summary: 'Execution not deleted',
                    detail: err?.error?.message || err?.error?.detail || 'The execution may now have linked activity.' });
                this.loadProject(project.id!);
            }
        });
    }

    exportDeliverySchedule(): void {
        const project = this.project();
        const companyId = project?.owningCompanyId || this.sessionStorageService.getCompany()?.id;
        if (!project?.id || !companyId) {
            return;
        }
        this.exportLoading.set(true);
        this.posmService.exportDeliverySchedules(project.id, companyId).subscribe({
            next: blob => {
                saveAs(blob, `posm-delivery-schedule-export-${project.projectNumber || project.id}.xlsx`);
                this.exportLoading.set(false);
            },
            error: () => {
                this.exportLoading.set(false);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Export failed',
                    detail: 'Unable to export the delivery schedule.'
                });
            }
        });
    }

    getSeverityForStatus(status: string) {
        if (!status) return 'info';
        if (status.includes('Pending') || status === 'warning') return 'warning';
        if (status.includes('Completed') || status === 'success') return 'success';
        if (status.includes('Alert') || status === 'danger') return 'danger';
        return 'info';
    }

    metricToneForProjectStatus(status?: string): 'default' | 'success' | 'warn' | 'info' | 'danger' {
        if (!status) return 'default';
        const normalized = status.toUpperCase();
        if (normalized.includes('COMPLETE') || normalized.includes('CLOSED')) return 'success';
        if (normalized.includes('PENDING') || normalized.includes('DRAFT')) return 'warn';
        if (normalized.includes('ERROR') || normalized.includes('CANCEL')) return 'danger';
        return 'info';
    }

    heroStatusClass(status?: string) {
        return `status-${(status || 'draft').toLowerCase().replaceAll('_', '-')}`;
    }

    tabBadge(tabId: string): string | null {
        const project = this.project();
        const summary = this.workspaceSummary();
        switch (tabId) {
            case 'overview':
                return project?.status || null;
            case 'materials':
                return this.materialItemCount() ? String(this.materialItemCount()) : null;
            case 'delivery-schedule':
                return this.deliveryScheduleCount() ? String(this.deliveryScheduleCount()) : null;
            case 'truck-alignment':
                return this.truckAlignmentReleaseCount() ? String(this.truckAlignmentReleaseCount()) : null;
            case 'execution':
                return this.executionLinkedDeliveryCount() ? String(this.executionLinkedDeliveryCount()) : null;
            case 'productivity':
                return summary?.openProductivityIncidents ? String(summary.openProductivityIncidents) : null;
            default:
                return null;
        }
    }

    tabBadgeTone(tabId: string): 'info' | 'success' | 'neutral' {
        switch (tabId) {
            case 'overview':
            case 'delivery-schedule':
            case 'execution':
                return 'info';
            case 'truck-alignment':
                return 'success';
            default:
                return 'neutral';
        }
    }

    detailSubtitle(project: PosmProject): string {
        const client = project.crmClient?.companyName || project.client?.name || 'No client';
        const brand = project.brandName || 'No brand';
        return `${client} | ${brand} | ${this.projectJobTypeDisplay(project)}`;
    }

    projectJobTypeGroup(project: PosmProject): string {
        const normalized = this.normalizeText(project.jobTypeGroup || this.projectJobTypes(project)[0] || project.jobType);
        if (['installation', 'delivery', 'removal', 'collection'].includes(normalized)) {
            return 'Logistics';
        }
        if (['refurbishment', 'rebranding'].includes(normalized)) {
            return 'Refurbishment and Rebranding';
        }
        if (['site survey', 'site_survey'].includes(normalized)) {
            return 'Site Survey';
        }
        return project.jobTypeGroup || 'Project';
    }

    projectJobTypes(project: PosmProject): string[] {
        const scopes = (project.jobTypes || [])
            .map(scope => this.normalizeJobTypeLabel(scope))
            .filter(Boolean);
        if (scopes.length) {
            return Array.from(new Set(scopes));
        }
        const legacy = this.normalizeJobTypeLabel(project.jobType);
        return legacy ? [legacy] : [];
    }

    projectJobTypeLabel(project: PosmProject): string {
        const scopes = this.projectJobTypes(project);
        return scopes.length ? scopes.join(' + ') : project.jobType || project.jobTypeGroup || 'General';
    }

    projectJobTypeDisplay(project: PosmProject): string {
        const group = this.projectJobTypeGroup(project);
        const type = this.projectJobTypeLabel(project);
        if (!type || this.normalizeText(group) === this.normalizeText(type)) {
            return group;
        }
        return `${group} - ${type}`;
    }

    loadTruckAlignmentBadge(projectId?: number, owningCompanyId?: number) {
        if (!projectId || !owningCompanyId) {
            this.truckAlignmentReleaseCount.set(0);
            return;
        }
        this.transportPlanningService.getPosmProjectReleases(projectId, owningCompanyId).subscribe({
            next: releases => this.truckAlignmentReleaseCount.set(releases?.length || 0),
            error: () => this.truckAlignmentReleaseCount.set(0)
        });
    }

    loadMaterialsBadge(projectId?: number, owningCompanyId?: number) {
        if (!projectId || !owningCompanyId) {
            this.materialItemCount.set(0);
            return;
        }
        this.posmService.getProjectItems(projectId, owningCompanyId).subscribe({
            next: items => this.materialItemCount.set(items?.length || 0),
            error: () => this.materialItemCount.set(0)
        });
    }

    private normalizeText(value: unknown): string {
        return `${value || ''}`.trim().toLowerCase();
    }

    private normalizeJobTypeLabel(value: unknown): string {
        const normalized = this.normalizeText(value);
        switch (normalized) {
            case 'installation':
                return 'Installation';
            case 'delivery':
                return 'Delivery';
            case 'removal':
                return 'Removal';
            case 'collection':
                return 'Collection';
            case 'refurbishment':
                return 'Refurbishment';
            case 'rebranding':
                return 'Rebranding';
            case 'site survey':
            case 'site_survey':
                return 'Site Survey';
            default:
                return `${value || ''}`.trim();
        }
    }
}
